import { doc, setDoc, updateDoc } from 'firebase/firestore';
import { ISessionWindowOption } from '../providers/settings/metricHistorySettingProvider';
import { testTypesProvider } from '../providers/testTypesProvider';
import { userAsPromise } from '../providers/userProvider';
import {
  EZScorePreference,
  IMetricMap,
  ISelectedLabelKey,
  ISelectOption,
  ITestType,
} from '../types';

let testTypes = [];

/**
 * subscribe to testTypes and cache results
 */
testTypesProvider().subscribe((mTestTypes: ITestType[]): void => {
  testTypes = mTestTypes;
});

export const toggleMetric = async (
  testTypeId: string,
  key: string,
  value: boolean
): Promise<void> => {
  const user = await userAsPromise();

  await updateDoc(
    doc(user.services.firestore, `userProfiles/${user.id}/settings/metrics`),
    {
      [`${testTypeId}.${key}`]: value,
    }
  );
};

/**
 * @description enables all metrics for the test type
 * @param testTypeId
 */
export const selectAllMetrics = async (testTypeId: string): Promise<void> => {
  const user = await userAsPromise();

  await setDoc(
    doc(user.services.firestore, `userProfiles/${user.id}/settings/metrics`),
    {
      [testTypeId]: {},
    },
    { merge: true }
  );
};

export const deselectAllMetrics = async (
  testTypeId: string,
  metrics: IMetricMap
): Promise<void> => {
  const update = Object.keys(metrics).reduce(
    (
      acc: { [index: string]: { [index: string]: boolean } },
      curr: string
    ): { [index: string]: { [index: string]: boolean } } => {
      if (metrics[curr].isExcludable) {
        acc[testTypeId][curr] = false;
      }
      return acc;
    },
    { [testTypeId]: {} }
  );
  const user = await userAsPromise();

  await setDoc(
    doc(user.services.firestore, `userProfiles/${user.id}/settings/metrics`),
    update,
    { merge: true }
  );
};

export const setImperialMetricSetting = async (
  imperial: boolean
): Promise<void> => {
  const user = await userAsPromise();

  await setDoc(
    doc(user.services.firestore, `userProfiles/${user.id}/settings/metrics`),
    { imperial },
    { merge: true }
  );
};

export const setLimitSessionSetting = async (
  limitSessionSetting: ISessionWindowOption
): Promise<void> => {
  const user = await userAsPromise();

  await setDoc(
    doc(
      user.services.firestore,
      `userProfiles/${user.id}/settings/limitSession`
    ),
    limitSessionSetting,
    { merge: true }
  );
};

export const setSelectedMetricMean = async (
  selectedMetricMean: ISelectedLabelKey
): Promise<void> => {
  const user = await userAsPromise();

  await setDoc(
    doc(
      user.services.firestore,
      `userProfiles/${user.id}/settings/selectedMetricMean`
    ),
    selectedMetricMean,
    { merge: true }
  );
};

export const updateKeyMetrics = async (
  testType: string,
  selectedMetric: ISelectOption
): Promise<void> => {
  const user = await userAsPromise();

  await setDoc(
    doc(user.services.firestore, `userProfiles/${user.id}/settings/keyMetrics`),
    { [testType]: selectedMetric },
    { merge: true }
  );
};

export const setZscorePreference = async (
  zScorePreference: EZScorePreference
): Promise<void> => {
  const user = await userAsPromise();

  await setDoc(
    doc(user.services.firestore, `userProfiles/${user.id}/settings/comparison`),
    { zScorePreference },
    { merge: true }
  );
};
